import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { tWithKeyAttr as t } from "../../../i18nHelper";
import i18n from "../../../i18n";
import Modal from "../../modals/modal/Modal";
import AddNewUserForm from "../../forms/addNewUserForm/addNewUserForm";
import { useAppSelector } from "../../../services/hooks";
import { cdrDataSelector } from "../../../services/selectors/callCenter.selectors";
import type { TCallDocumentContext } from "../../../types/callCenter.types";
import type {
  TTherapistWithUsers,
  TUsersWithOutTherapist,
} from "../../../services/types/user.types";
import styles from "./PostCallDocumentSection.module.css";

/**
 * How long after the call ends before the recordings screen is worth opening — the
 * provider needs a few minutes to publish the file. Must stay in step with the
 * CC_RECORDINGS_NOT_READY message shown while the button is disabled.
 */
const RECORDINGS_NAV_DELAY_MS = 5 * 60_000;

function isRecordingsNavigationReady(endTime: string): boolean {
  const endMs = new Date(endTime).getTime();
  if (Number.isNaN(endMs)) return true;
  return Date.now() - endMs >= RECORDINGS_NAV_DELAY_MS;
}

function displayCallDirectionLabel(
  direction: string,
  translate: (key: string) => string
): string {
  const d = direction?.toLowerCase?.() ?? "";
  if (d === "inbound") return translate("CC_INBOUND_CALL");
  if (d === "outbound") return translate("CC_OUTBOUND_CALL");
  return direction || "";
}

function patientDisplayName(
  inquiry: TTherapistWithUsers | TUsersWithOutTherapist
): string {
  const fromParts = `${inquiry.firstName ?? ""} ${inquiry.lastName ?? ""}`.trim();
  if (fromParts) return fromParts;
  return inquiry.fullName?.trim() || inquiry.usersName?.trim() || "";
}

export interface IPostCallDocumentSectionProps {
  callData: TCallDocumentContext;
  inquiry: TTherapistWithUsers | TUsersWithOutTherapist;
  onCloseDrawer?: () => void;
}

/**
 * CDR capture, recording link, and unidentified-caller add-user flow.
 * Rendered above the standard inquiry document body when opened from a call.
 */
const PostCallDocumentSection: React.FC<IPostCallDocumentSectionProps> = ({
  callData,
  inquiry,
  onCloseDrawer,
}) => {
  const navigate = useNavigate();
  const cdrFromStore = useAppSelector(cdrDataSelector);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [canOpenRecordings, setCanOpenRecordings] = useState(() =>
    isRecordingsNavigationReady(callData.cdr.endTime)
  );

  const recordNumber =
    cdrFromStore?.callId === callData.cdr.callId
      ? cdrFromStore.recordNumber
      : callData.cdr.recordNumber;

  const canNavigateToUser =
    !callData.isUnidentified && Boolean(inquiry.guid?.trim()) && inquiry.usersId > 0;

  useEffect(() => {
    setShowAddUserModal(false);
  }, [callData.cdr.callId]);

  useEffect(() => {
    const endMs = new Date(callData.cdr.endTime).getTime();
    if (Number.isNaN(endMs)) {
      setCanOpenRecordings(true);
      return;
    }

    const elapsed = Date.now() - endMs;
    if (elapsed >= RECORDINGS_NAV_DELAY_MS) {
      setCanOpenRecordings(true);
      return;
    }

    setCanOpenRecordings(false);
    const timer = window.setTimeout(() => {
      setCanOpenRecordings(true);
    }, RECORDINGS_NAV_DELAY_MS - elapsed);

    return () => window.clearTimeout(timer);
  }, [callData.cdr.callId, callData.cdr.endTime]);

  const navigateAndClose = (path: string, state: Record<string, unknown>) => {
    onCloseDrawer?.();
    navigate(path, { state });
  };

  const goToRecordings = () => {
    if (!canNavigateToUser || !canOpenRecordings) return;
    navigateAndClose("/users/recordTable", {
      Guid: inquiry.guid,
      UsersId: inquiry.usersId,
      patientName: patientDisplayName(inquiry),
      patientPhone: inquiry.phone,
      patientBirth: inquiry.birth,
      StatusId: inquiry.status,
    });
  };

  const goToAnalysis = () => {
    if (!canNavigateToUser || !recordNumber) return;
    navigateAndClose(`/users/summaryMeeting/${recordNumber}`, {
      Guid: inquiry.guid,
      UsersId: inquiry.usersId,
    });
  };

  return (
    <>
      <h5 dir={i18n.dir()} className={styles.sectionTitle}>
        📞 {t("CC_CALL_DETAILS")}
      </h5>
      <div className={styles.callDetailsSection}>
        <div className={styles.autoCallData}>
          <h6 className={styles.callSectionTitle}>נתונים אוטומטיים</h6>
          <div className={styles.autoDataList}>
            <div className={styles.autoDataItem}>
              <span className={styles.autoDataLabel}>
                <span className={styles.autoDataIcon} aria-hidden="true">
                  📅
                </span>
                {t("CC_CALL_DATE")}:
              </span>
              <span className={styles.autoDataValue}>
                {new Date(callData.cdr.startTime).toLocaleDateString("he-IL", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                })}
              </span>
            </div>
            <div className={styles.autoDataItem}>
              <span className={styles.autoDataLabel}>
                <span className={styles.autoDataIcon} aria-hidden="true">
                  🕒
                </span>
                {t("CC_CALL_TIME")}:
              </span>
              <span className={styles.autoDataValue}>
                {new Date(callData.cdr.startTime).toLocaleTimeString("he-IL", {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </span>
            </div>
            <div className={styles.autoDataItem}>
              <span className={styles.autoDataLabel}>
                <span className={styles.autoDataIcon} aria-hidden="true">
                  ⏱
                </span>
                {t("CC_DURATION")}:
              </span>
              <span className={styles.autoDataValue}>
                {(() => {
                  const hrs = Math.floor(callData.cdr.duration / 3600);
                  const mins = Math.floor((callData.cdr.duration % 3600) / 60);
                  const secs = callData.cdr.duration % 60;
                  return hrs > 0
                    ? `${hrs}:${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`
                    : `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
                })()}
              </span>
            </div>
            <div className={styles.autoDataItem}>
              <span className={styles.autoDataLabel}>
                <span className={styles.autoDataIcon} aria-hidden="true">
                  📞
                </span>
                {t("CC_CALL_DIRECTION")}:
              </span>
              <span className={styles.autoDataValue}>
                {displayCallDirectionLabel(callData.cdr.direction, t)}
              </span>
            </div>
            {(callData.cdr.recordingUrl?.trim() || callData.cdr.wasRecorded) && (
              <div
                className={`${styles.autoDataItem} ${styles.autoDataItemFull}`}
              >
                <span className={styles.autoDataLabel}>
                  <span className={styles.autoDataIcon} aria-hidden="true">
                    🎙️
                  </span>
                  {t("CC_RECORDING_URL")}:
                </span>
                <div className={styles.recordingUrlBlock}>
                  {callData.cdr.recordingUrl?.trim() ? (
                    <a
                      className={styles.recordingUrlLink}
                      href={callData.cdr.recordingUrl.trim()}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t("CC_OPEN_RECORDING")}
                    </a>
                  ) : (
                    <span
                      className={`${styles.autoDataValue} ${styles.recordingBadge}`}
                    >
                      ✓ {t("CC_RECORDING_AVAILABLE")}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {canNavigateToUser && (
          <div className={styles.callActions}>
            <button
              type="button"
              className={`button hover ${styles.callActionBtn}`}
              onClick={goToRecordings}
              disabled={!canOpenRecordings}
              title={!canOpenRecordings ? t("CC_RECORDINGS_NOT_READY") : undefined}
            >
              📋 {t("CC_OPEN_RECORDINGS")}
            </button>
            {/* The provider needs a few minutes to publish the file — say so, otherwise
                a greyed-out button reads as a bug. */}
            {!canOpenRecordings && (
              <span
                className={styles.callActionHint}
                role="status"
                data-testid="recordings-not-ready-hint"
              >
                {t("CC_RECORDINGS_NOT_READY")}
              </span>
            )}
            {recordNumber != null && recordNumber > 0 && (
              <button
                type="button"
                className={`button hover ${styles.callActionBtn} ${styles.callActionBtnPrimary}`}
                onClick={goToAnalysis}
              >
                📊 {t("CC_VIEW_ANALYSIS")}
              </button>
            )}
          </div>
        )}

        {callData.isUnidentified && (
          <div className={styles.unidentifiedCallActions}>
            <p className={styles.unidentifiedPhoneRow}>
              <strong>{t("CC_MOBILE")}:</strong>{" "}
              <span dir="ltr">{callData.callerPhone}</span>
            </p>
            <button
              type="button"
              className={`button hover ${styles.addUserFromCallBtn}`}
              onClick={() => setShowAddUserModal(true)}
            >
              ➕ {t("ADD_USER")}
            </button>
            <p className={styles.unidentifiedHint}>
              {t("CC_UNIDENTIFIED_ADD_USER_HINT")}
            </p>
          </div>
        )}

        {callData.isUnidentified && showAddUserModal && (
          <Modal
            onClose={() => setShowAddUserModal(false)}
            showCloseButton={false}
            prevencherAnswer
          >
            <AddNewUserForm
              onClose={() => setShowAddUserModal(false)}
              defaultPhone={callData.callerPhone}
            />
          </Modal>
        )}

      </div>
    </>
  );
};

export default PostCallDocumentSection;
